// The SINGLE source of truth for the "guided build" Q&A — the questions, the answer model, the
// opening-message pre-fill, the prompt assembly, the site-scoped persistence, and the site-aware
// brand rule. Every guided-build surface renders from THIS module so they can't drift:
//   • the inline Studio chat interview (useStudioPage)
//   • the GuidedStartDialog modal (dashboard "guided start")
//   • the /build wizard's SiteBrandControl (for `resolveBrandForSite`)
// Pure except `persistGuidedEssentials` (a thin, SITE-SCOPED API wrapper). No React here.

import { updateSiteConfig } from '@/api/site-config';
import { upsertSiteIntegration } from '@/api/site-integrations';
import { buildGuidedPrompt } from '@/lib/guidedPrompt';
import { extractBuildFacts } from '@/lib/chatCommands';
import { createBrand, updateBrandColors, updateBrandFonts } from '@/api/brands';
import { uploadGeneratedLogoSet } from '@/lib/logoApply';
import type { SiteConfig } from '@/types';
import type { SiteRecord } from '@/api/sites';
import type { BrandSummary } from '@/api/brands';
import type { BrandKit, GeneratedLogoSet } from '@/api/ai';

// ── Shared question data (moved out of GuidedStartDialog so both surfaces use one copy) ──
export type StyleKey = 'luxury' | 'modern' | 'coastal' | 'bold' | 'minimal';

export const STYLES: { key: StyleKey; label: string; blurb: string }[] = [
  { key: 'luxury', label: 'Luxury', blurb: 'Rich, high-end, elegant' },
  { key: 'modern', label: 'Modern', blurb: 'Clean lines, lots of space' },
  { key: 'coastal', label: 'Coastal', blurb: 'Light, airy, beachy' },
  { key: 'bold', label: 'Bold', blurb: 'Strong color, big type' },
  { key: 'minimal', label: 'Minimal', blurb: 'Simple and understated' },
];

export const COLORS: { hex: string; label: string }[] = [
  { hex: '#1f3a5f', label: 'Navy' },
  { hex: '#1e293b', label: 'Charcoal' },
  { hex: '#14532d', label: 'Forest' },
  { hex: '#7f1d1d', label: 'Burgundy' },
  { hex: '#b45309', label: 'Amber' },
  { hex: '#0f766e', label: 'Teal' },
  { hex: '#4E1F60', label: 'Plum' },
];

// Home "looks" — a curated subset of the HOME_LOOK_PRESETS the /looks page offers (variant_keys the
// gateway seeds into section_templates). The chat Look step writes `section_looks.home` = one of
// these, the SAME store Pick-a-Look uses; the rest stay reachable there. Keys must match real presets.
export const LOOKS: { key: string; label: string; blurb: string }[] = [
  { key: 'noir', label: 'Noir', blurb: 'Dark, luxe, high-contrast' },
  { key: 'coastal', label: 'Coastal', blurb: 'Light, airy, breezy' },
  { key: 'heritage', label: 'Heritage', blurb: 'Classic serif, timeless' },
  { key: 'vanguard', label: 'Vanguard', blurb: 'Modern, bold, clean lines' },
  { key: 'homestead', label: 'Homestead', blurb: 'Warm, earthy, welcoming' },
  { key: 'signature', label: 'Signature', blurb: 'Editorial, refined' },
];

// Agent-card styles — mirrors CardStylePage's AGENT_CARD_STYLES. The chat Cards step writes
// `section_looks.agentCard` (renders site-wide), the SAME store the /cards page uses.
export const AGENT_CARDS: { key: string; label: string; blurb: string }[] = [
  { key: 'classic', label: 'Classic', blurb: 'Photo, name, details stacked' },
  { key: 'portrait', label: 'Portrait', blurb: 'Tall photo-forward card' },
  { key: 'row', label: 'Row', blurb: 'Photo beside the details' },
  { key: 'minimal', label: 'Minimal', blurb: 'Clean, understated' },
  { key: 'overlay', label: 'Overlay', blurb: 'Text over the photo' },
  { key: 'framed', label: 'Framed', blurb: 'Bordered, formal' },
];

// A tasteful Google-font pairing per vibe, so a NEW brand created in chat has real fonts (not just a
// color). Applied via updateBrandFonts (heading + body rows), the same as the Brand Manager.
const FONT_PAIRINGS: Record<string, { heading: string; body: string }> = {
  luxury: { heading: 'Playfair Display', body: 'Inter' },
  modern: { heading: 'Montserrat', body: 'Inter' },
  coastal: { heading: 'Poppins', body: 'Inter' },
  bold: { heading: 'Archivo', body: 'Inter' },
  minimal: { heading: 'Inter', body: 'Inter' },
};
export function fontPairingForStyle(style?: string | null): { heading: string; body: string } {
  return FONT_PAIRINGS[(style ?? '').toLowerCase()] ?? { heading: 'Montserrat', body: 'Inter' };
}

// ── Armed-chip sentinels — a tapped option's `prompt` encodes an answer for the current step, so
//    the chat records it and advances instead of running it through the normal command parsers. ──
const SENTINEL_RE = /^⟦gi:([a-z]+)=([\s\S]*)⟧$/;
export function interviewSentinel(field: string, value: string): string {
  return `⟦gi:${field}=${value}⟧`;
}
export function parseInterviewSentinel(prompt: string): { field: string; value: string } | null {
  const m = prompt.trim().match(SENTINEL_RE);
  return m ? { field: m[1], value: m[2] } : null;
}

// ── Answer model ──
export interface GuidedAnswers {
  style?: string | null; // a vibe/style word (StyleKey or any matchVibe key) — drives the build look
  name?: string;
  market?: string;
  email?: string;
  phone?: string;
  mls?: string;
  mapbox?: string; // a Mapbox public token → interactive listing map (site integration)
  color?: string | null; // hex primary — only meaningful when starting a NEW brand
  brand?: { mode: 'reuse' | 'new'; brandId?: string }; // the brand resolved for THIS site
  look?: string; // a home look variant_key (section_looks.home) — overrides the vibe default
  agentCard?: string; // an agent-card style (section_looks.agentCard) — site-wide
  heroImage?: string; // a hosted hero photo URL the user picked (Pexels / upload / paste) — '' = none

  // From an AI brand kit (brandSuggest) the user picked — a FULL palette + font pairing + voice,
  // written into the created brand. When present these win over the single manual color / vibe fonts.
  brandColors?: { role: string; hex_value: string; label?: string }[];
  fonts?: { heading?: string; body?: string };
  voice?: string;
  tone?: string[];
  logo?: GeneratedLogoSet; // an AI-generated logo set the user chose — uploaded to the brand on build

  // The privacy policy — 'published' once the agent published one from the legal form, '' when
  // skipped. The form writes straight to the site's legal profile (site-scoped, same as Settings →
  // Privacy Policy), so nothing about it is replayed at build time; this only records that the
  // question was asked and answered.
  legal?: string;

  /**
   * A headline the OPENING message asked for, and whether the agent confirmed it.
   *
   * `headlineCandidate` is what we parsed; `headline` is what they agreed to ('' = declined).
   * They are separate because "we think you said this" and "you told us to use this" are
   * different states, and collapsing them would either silently apply a guess or forget it.
   *
   * This exists because the interview used to throw the opening prompt's specifics away. Asked
   * to "build a home page with the headline 'Second Site Headline'", it ran nine questions,
   * never mentioned the headline, and shipped the look's stock copy. The business name survived
   * only because a step happens to ask for it.
   */
  headlineCandidate?: string;
  headline?: string;
}

/**
 * A headline the user quoted in their opening message.
 *
 * Deliberately narrow: only a QUOTED string next to the word "headline"/"title"/"hero". An
 * unquoted guess would turn half a sentence into an h1, which is worse than not asking — and
 * the confirm step only earns its extra turn when the candidate is usually right.
 *
 * Handles straight and curly quotes, since a phone keyboard produces the curly ones.
 */
export function extractHeadlineRequest(prompt: string): string | undefined {
  const m = prompt.match(
    /\b(?:headline|header|title|hero(?:\s+text)?)\b[^"“”']{0,24}["“']([^"“”']{3,120})["”']/i,
  );
  const found = m?.[1]?.trim();
  return found ? found : undefined;
}

/**
 * The opening message is "answers already given" — parse everything the user told us up front so
 * the interview only asks for the gaps (and can build with zero questions when it's all there).
 */
export function prefillFromPrompt(prompt: string): GuidedAnswers {
  const f = extractBuildFacts(prompt);
  const a: GuidedAnswers = {};
  if (f.vibe) a.style = f.vibe;
  if (f.firmName) a.name = f.firmName;
  if (f.market) a.market = f.market;
  if (f.colors?.primary) a.color = f.colors.primary;
  // Parsed, NOT applied — the headline step confirms it before anything is built.
  const headline = extractHeadlineRequest(prompt);
  if (headline) a.headlineCandidate = headline;
  return a;
}

// ── Steps ──
export type StepId = 'style' | 'name' | 'contact' | 'brand' | 'color' | 'logo' | 'look' | 'cards' | 'media' | 'mls' | 'mapbox' | 'legal' | 'headline';

export interface InterviewStep {
  id: StepId;
  kind: 'options' | 'text' | 'brand' | 'media' | 'legal';
  optional: boolean; // shows a "Skip" affordance
  /** Relevant given the answers so far (e.g. the color swatch only when starting a NEW brand). */
  applies: (a: GuidedAnswers) => boolean;
  isAnswered: (a: GuidedAnswers) => boolean;
}

export const INTERVIEW_STEPS: InterviewStep[] = [
  { id: 'style', kind: 'options', optional: false, applies: () => true, isAnswered: (a) => !!a.style },
  { id: 'name', kind: 'text', optional: false, applies: () => true, isAnswered: (a) => a.name !== undefined },
  // Only when the opening message actually quoted one. Optional so "Skip" reads as "don't use it".
  { id: 'headline', kind: 'options', optional: true, applies: (a) => !!a.headlineCandidate, isAnswered: (a) => a.headline !== undefined },
  { id: 'contact', kind: 'text', optional: true, applies: () => true, isAnswered: (a) => a.email !== undefined || a.phone !== undefined },
  { id: 'brand', kind: 'brand', optional: false, applies: () => true, isAnswered: (a) => a.brand !== undefined },
  { id: 'color', kind: 'options', optional: true, applies: (a) => a.brand?.mode === 'new', isAnswered: (a) => a.color !== undefined },
  { id: 'logo', kind: 'options', optional: true, applies: (a) => a.brand?.mode === 'new', isAnswered: (a) => a.logo !== undefined },
  // The separate 'look' (home-page design) step is COLLAPSED into 'style': the vibe already drives the
  // home look via scaffoldLookForVibe(), so we don't ask the same "which style" question twice. a.look
  // can still be set programmatically (it overrides the vibe default), but the interview no longer asks.
  { id: 'cards', kind: 'options', optional: true, applies: () => true, isAnswered: (a) => a.agentCard !== undefined },
  // Hero photo — can't be pre-filled from text, so it's always asked (Skip to keep the look's default).
  { id: 'media', kind: 'media', optional: true, applies: () => true, isAnswered: (a) => a.heroImage !== undefined },
  { id: 'mls', kind: 'text', optional: true, applies: () => true, isAnswered: (a) => a.mls !== undefined },
  // Mapbox public token → interactive listing map (the /build wizard's Integrations step offers this too).
  { id: 'mapbox', kind: 'text', optional: true, applies: () => true, isAnswered: (a) => a.mapbox !== undefined },
  // LAST, deliberately. Every site's footer links to /privacy-policy, and most states also require
  // the brokerage's licence details on the site — so the question belongs in the path to a finished
  // site, not only in Settings where it gets found by whoever went looking. It opens the same form
  // the /build wizard's Legal step uses. Optional: an agent who doesn't have their licence number to
  // hand must not be blocked from building.
  { id: 'legal', kind: 'legal', optional: true, applies: () => true, isAnswered: (a) => a.legal !== undefined },
];

/**
 * The next step to ask, or null when we have enough to build. A step is "done" when it doesn't
 * apply, is already answered (incl. pre-filled), or the user skipped it. Shared by the chat + modal
 * so the two can't diverge on ordering or skip logic.
 */
export function nextUnansweredStep(
  a: GuidedAnswers,
  skipped: ReadonlySet<StepId> = new Set(),
): InterviewStep | null {
  return INTERVIEW_STEPS.find((s) => s.applies(a) && !s.isAnswered(a) && !skipped.has(s.id)) ?? null;
}

/** Record a step's answer into the running answers (pure). `value` is a chip's sentinel value or
 *  the user's typed reply. Contact free-text is split into email + phone. */
export function recordInterviewAnswer(a: GuidedAnswers, stepId: StepId, value: string): void {
  switch (stepId) {
    case 'style':
      a.style = value.trim();
      break;
    case 'name':
      a.name = value.trim();
      break;
    case 'contact': {
      a.email = value.match(/[\w.+-]+@[\w-]+\.[\w.-]+/)?.[0] ?? '';
      a.phone = value.match(/[\d][\d().+\s-]{6,}\d/)?.[0]?.trim() ?? '';
      break;
    }
    case 'color':
      a.color = value.trim();
      break;
    case 'look':
      a.look = value.trim();
      break;
    case 'cards':
      a.agentCard = value.trim();
      break;
    case 'media':
      a.heroImage = value.trim(); // a hosted URL from the picker (or '' when the user picked nothing)
      break;
    case 'mls':
      a.mls = value.trim();
      break;
    case 'mapbox':
      a.mapbox = value.trim(); // a Mapbox public token (pk.…) — '' when skipped
      break;
    case 'legal':
      a.legal = value.trim(); // 'published' — the form already wrote to the site's legal profile
      break;
    case 'brand':
      a.brand = value === '__new' ? { mode: 'new' } : { mode: 'reuse', brandId: value };
      break;
    case 'headline':
      // '__skip' (the Skip chip) and '__no' both mean "don't use it" — recorded as '' so the
      // step counts as answered and never asks twice. Anything else is the agreed text: the
      // confirm chip sends the candidate back, and free text lets them correct it in place.
      a.headline = value === '__skip' || value === '__no' ? '' : value.trim();
      break;
  }
}

// ── Build prompt (reuses buildGuidedPrompt so parseScaffoldSite recognizes it) ──
export function assembleBuildPrompt(a: GuidedAnswers): string {
  const base = buildGuidedPrompt({ style: a.style ?? null, name: a.name ?? '', market: a.market ?? '' });
  // A confirmed headline is an instruction, not a preference — state it last so it is the most
  // recent thing the generator reads. Declined ('') adds nothing.
  const headline = a.headline?.trim();
  return headline ? `${base}\n\nUse exactly this as the home page hero heading: "${headline}"` : base;
}

/**
 * A one-line acknowledgment of what the opening prompt already told us (business name / market), so
 * the interview visibly confirms it captured them instead of silently skipping those steps — the
 * "it didn't even add the name" complaint. Empty when the prompt named neither.
 */
export function buildInterviewIntro(a: GuidedAnswers): string {
  const name = a.name?.trim();
  const market = a.market?.trim();
  if (name && market) return `Great — a site for ${name} in ${market}.`;
  if (name) return `Great — a site for ${name}.`;
  if (market) return `Great — a site for the ${market} market.`;
  return '';
}

/** Human-readable "here's what I'll build" lines — shared so the chat + modal show the same plan. */
export function buildPlanLines(a: GuidedAnswers): string[] {
  const styleWord = a.style || 'modern';
  const who = a.name?.trim() ? ` for ${a.name.trim()}` : '';
  const where = a.market?.trim() ? ` in ${a.market.trim()}` : '';
  return [
    `A ${styleWord} real-estate website${who}${where}`,
    'Home with a hero + featured listings, plus Search, About, and Contact pages',
    a.mls?.trim() ? `Your listings & agents from MLS ${a.mls.trim()}` : 'Your MLS listings & agents connected',
    (a.email?.trim() || a.phone?.trim())
      ? `Your contact details${a.email?.trim() ? ` (${a.email.trim()})` : ''} on the site`
      : 'Your contact section',
  ];
}

// ── Persistence — SITE-SCOPED ONLY (never the tenant profile, which every site shares) ──
/**
 * Persist the guided answers to the ACTIVE site (via the `X-Site-Id` header the api client attaches)
 * so the scaffold + live site pick them up. Contact + brand color → the site's config; MLS → the
 * site's MLS integration (matching the wizard's Integrations step). The business name flows into the
 * build via the assembled prompt (`applyTemplateBySlug` stamps it), NOT a tenant-wide profile write.
 * Best-effort — a failure must never block the build.
 */
export async function persistGuidedEssentials(a: GuidedAnswers): Promise<void> {
  const tasks: Promise<unknown>[] = [];

  if (a.email?.trim() || a.phone?.trim() || a.color) {
    tasks.push(
      updateSiteConfig((c) => {
        const footer = { ...(c.footer as Record<string, unknown> | undefined) } as Record<string, unknown>;
        if (a.email?.trim()) footer.contact_email = a.email.trim();
        if (a.phone?.trim()) footer.contact_phone = a.phone.trim();
        return {
          ...c,
          style: a.color ? { ...c.style, color_primary: a.color } : c.style,
          footer: footer as unknown as SiteConfig['footer'],
        };
      }),
    );
  }

  if (a.mls?.trim()) {
    tasks.push(upsertSiteIntegration('mls_reso', { credentials: { mls_id: a.mls.trim() }, is_enabled: true }));
  }

  // Mapbox public token → interactive listing map (same site-scoped integration the /build wizard writes).
  if (a.mapbox?.trim()) {
    tasks.push(upsertSiteIntegration('mapbox', { credentials: { accessToken: a.mapbox.trim() }, is_enabled: true }));
  }

  if (tasks.length) await Promise.allSettled(tasks);
}

/**
 * Create a REAL brand from the guided answers, writing the SAME tables the Brand Manager does
 * (`brands` + `brand_colors` + `brand_fonts` via createBrand/updateBrandColors/updateBrandFonts) so a
 * brand built in chat shows up in the Brand Manager as a first-class brand — no second source of
 * truth. Sets the name (+ contact) at create, the primary color, and a vibe-matched font pairing.
 * Returns the new brand id (the caller links it to the site + applies it), or null if creation failed
 * (the build then proceeds brand-less rather than erroring).
 */
export async function createGuidedBrand(a: GuidedAnswers): Promise<string | null> {
  const name = a.name?.trim();
  if (!name) return null;
  try {
    const brand = await createBrand({
      name,
      default_email: a.email?.trim() || undefined,
      default_phone: a.phone?.trim() || undefined,
      // Voice/tone come from an AI brand kit (brandSuggest) the user picked, if any.
      brand_voice: a.voice?.trim() || undefined,
      tone_keywords: a.tone?.length ? a.tone : undefined,
    });
    // Colors: a full AI-kit palette wins; else the single manual primary. All hexes 6-digit-validated.
    const kitColors = (a.brandColors ?? [])
      .filter((c) => /^#[0-9a-fA-F]{6}$/.test(c.hex_value))
      .map((c, i) => ({ role: c.role, hex_value: c.hex_value, label: c.label, sort_order: i }));
    const colorRows =
      kitColors.length > 0
        ? kitColors
        : a.color && /^#[0-9a-fA-F]{6}$/.test(a.color)
          ? [{ role: 'primary', hex_value: a.color, label: 'Primary', sort_order: 0 }]
          : [];
    if (colorRows.length) await updateBrandColors(brand.id, colorRows).catch(() => {});
    // Fonts: an AI-kit pairing wins; else a vibe-matched default.
    const pairing = fontPairingForStyle(a.style);
    const fonts = {
      heading: a.fonts?.heading || pairing.heading,
      body: a.fonts?.body || pairing.body,
    };
    await updateBrandFonts(brand.id, [
      { role: 'heading', family: fonts.heading, source: 'google' },
      { role: 'body', family: fonts.body, source: 'google' },
    ]).catch(() => {});
    // An AI-generated logo the user chose → upload to the brand's logo slots (bg-removed), best-effort.
    if (a.logo) {
      const safe = name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'logo';
      await uploadGeneratedLogoSet(brand.id, a.logo, safe).catch(() => {});
    }
    return brand.id;
  } catch {
    return null;
  }
}

/**
 * Fold a chosen AI brand kit (a brandSuggest board) into the answers — its primary color, full
 * palette, heading/body fonts, and voice/tone — so createGuidedBrand writes all of it to the brand.
 */
export function applyBrandKit(a: GuidedAnswers, kit: BrandKit): void {
  const primary = kit.colors?.find((c) => c.role === 'primary')?.hex_value;
  if (primary) a.color = primary;
  if (Array.isArray(kit.colors) && kit.colors.length) {
    a.brandColors = kit.colors.map((c) => ({ role: c.role, hex_value: c.hex_value, label: c.label }));
  }
  const heading = kit.fonts?.find((f) => f.role === 'heading')?.family;
  const body = kit.fonts?.find((f) => f.role === 'body')?.family;
  if (heading || body) a.fonts = { heading, body };
  if (kit.voice) a.voice = kit.voice;
  if (Array.isArray(kit.toneKeywords) && kit.toneKeywords.length) a.tone = kit.toneKeywords;
}

// ── Site-aware brand rule — never overwrite a site with a brand that isn't its own ──
export type BrandResolution =
  | { mode: 'use'; brandId: string; brandName: string } // the site already has, or uniquely gets, a brand
  | { mode: 'new' } // no brands exist → the scaffold makes a fresh default brand for this site
  | { mode: 'confirm'; brand: BrandSummary; otherSiteName: string | null } // one brand — ASK before applying (otherSiteName set ⇒ it's another site's brand; null ⇒ the tenant's only brand)
  | { mode: 'pick'; brands: BrandSummary[] }; // several brands → let the user choose

/**
 * Decide which brand a from-scratch build should apply to `siteId`, using the tenant's brands + the
 * per-site `brand_id` links. Pure (no fetch) so the chat, the modal, and the wizard's SiteBrandControl
 * all derive the SAME decision. It never mutates anything — the caller links the chosen brand to the
 * site (`updateSite(siteId,{brand_id})`) only after the user confirms/picks.
 */
export function resolveBrandForSite(opts: {
  siteId: string | null;
  sites: SiteRecord[];
  brands: BrandSummary[];
}): BrandResolution {
  const { siteId, sites, brands } = opts;
  const thisSite = siteId ? sites.find((s) => s.id === siteId) : undefined;

  // 1. Site already has its OWN brand → use it silently.
  if (thisSite?.brand_id) {
    const own = brands.find((b) => b.id === thisSite.brand_id);
    if (own) return { mode: 'use', brandId: own.id, brandName: own.name };
  }
  // 2. No brands at all → start fresh (the scaffold auto-creates a default brand).
  if (brands.length === 0) return { mode: 'new' };
  // 3. Exactly one brand.
  if (brands.length === 1) {
    const only = brands[0];
    const otherSite = sites.find((s) => s.id !== siteId && s.brand_id === only.id);
    // ALWAYS ask before applying the tenant's only brand to a from-scratch build — the agent may want a
    // fresh brand for THIS site (with its own colors/logo). Previously a single un-linked brand was
    // silently applied, so the interview never asked about the brand + never offered the brand wizard.
    // otherSiteName carries "it's used on {site}" context when relevant, else null (just "use or new?").
    return { mode: 'confirm', brand: only, otherSiteName: otherSite?.name ?? null };
  }
  // 4. Several brands → the user picks which one.
  return { mode: 'pick', brands };
}
